import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: unknown) => void;
          renderButton: (element: Element, config: unknown) => void;
        };
      };
    };
  }
}

type AuthMode = 'login' | 'register';

interface LoginFormData {
  username: string;
  password: string;
}

interface RegisterFormData {
  username: string;
  email: string;
  name: string;
  password: string;
  confirmPassword: string;
}

const AuthForm: React.FC = () => {
  const { login, register, loginWithPassword, authConfig } = useAuth();
  const supportsPasswordAuth = authConfig?.supports_password_auth !== false;
  const [mode, setMode] = useState<AuthMode>('login');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [loginData, setLoginData] = useState<LoginFormData>({
    username: '',
    password: ''
  });
  
  const [registerData, setRegisterData] = useState<RegisterFormData>({
    username: '',
    email: '',
    name: '',
    password: '',
    confirmPassword: ''
  });

  const handleGoogleResponse = useCallback(async (response: { credential: string }) => {
    try {
      setIsLoading(true);
      setError('');
      await login(response.credential);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Google login failed');
    } finally {
      setIsLoading(false);
    }
  }, [login]);

  const loadGoogleScript = useCallback(async () => {
    if (window.google?.accounts?.id) {
      setIsGoogleLoaded(true);
      return;
    }

    try {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      
      script.onload = () => {
        if (window.google?.accounts?.id) {
          setIsGoogleLoaded(true);
        }
      };
      
      document.head.appendChild(script);
    } catch (error) {
      console.error('Failed to load Google script:', error);
    }
  }, []);

  const initializeGoogle = useCallback(() => {
    if (!authConfig?.google_client_id || !window.google?.accounts?.id) return;

    window.google.accounts.id.initialize({
      client_id: authConfig.google_client_id,
      callback: handleGoogleResponse,
    });

    const buttonElement = document.getElementById('google-signin-button');
    if (buttonElement) {
      buttonElement.innerHTML = '';
      window.google.accounts.id.renderButton(buttonElement, {
        theme: 'outline',
        size: 'large',
        width: 400,
        text: 'continue_with'
      });
    }
  }, [authConfig?.google_client_id, handleGoogleResponse]);

  useEffect(() => {
    if (authConfig?.auth_enabled && !isGoogleLoaded) {
      loadGoogleScript();
    }
  }, [authConfig, isGoogleLoaded, loadGoogleScript]);

  useEffect(() => {
    if (isGoogleLoaded && authConfig?.google_client_id) {
      initializeGoogle();
    }
  }, [isGoogleLoaded, authConfig?.google_client_id, initializeGoogle]);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supportsPasswordAuth) return;
    setIsLoading(true);
    setError('');

    try {
      await loginWithPassword(loginData.username, loginData.password);
      // Auth context will handle the state update
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supportsPasswordAuth) return;
    setIsLoading(true);
    setError('');

    // Client-side validation
    if (registerData.username.length < 3) {
      setError('Username must be at least 3 characters long');
      setIsLoading(false);
      return;
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(registerData.username)) {
      setError('Username can only contain letters, numbers, underscores, and hyphens');
      setIsLoading(false);
      return;
    }
    if (registerData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      setIsLoading(false);
      return;
    }
    if (!/[A-Za-z]/.test(registerData.password)) {
      setError('Password must contain at least one letter');
      setIsLoading(false);
      return;
    }
    if (!/\d/.test(registerData.password)) {
      setError('Password must contain at least one number');
      setIsLoading(false);
      return;
    }
    if (registerData.password !== registerData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      await register({
        username: registerData.username,
        email: registerData.email,
        name: registerData.name,
        password: registerData.password,
      });
      // Auth context will handle the state update
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoginData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRegisterData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // Show loading state while auth config is being fetched
  if (!authConfig) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Welcome to Clazzo
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Your personal productivity companion
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 space-y-6">
          {/* Auth Mode Toggle */}
          {supportsPasswordAuth ? (
            <div className="flex rounded-lg bg-gray-100 p-1">
              <button
                onClick={() => setMode('login')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  mode === 'login'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => setMode('register')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  mode === 'register'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Sign Up
              </button>
            </div>
          ) : (
            <p className="text-center text-sm text-gray-600">
              This is a private app. Sign in with the authorized Google account.
            </p>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Google Sign-in */}
          {authConfig.auth_enabled && (
            <div className="space-y-4">
              <div id="google-signin-button" className="w-full min-h-[44px] flex items-center justify-center">
                {!isGoogleLoaded && (
                  <div className="flex items-center space-x-2 py-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-sm text-gray-500">Loading Google Sign-In...</span>
                  </div>
                )}
              </div>
              
              {supportsPasswordAuth && (
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">or</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Username/Password Forms */}
          {supportsPasswordAuth && (mode === 'login' ? (
            <form onSubmit={handleLoginSubmit} className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                  Username or Email
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  value={loginData.username}
                  onChange={handleLoginChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your username or email"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={loginData.password}
                    onChange={handleLoginChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleRegisterSubmit} className="space-y-4">
              <div>
                <label htmlFor="reg-username" className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  id="reg-username"
                  name="username"
                  type="text"
                  required
                  value={registerData.username}
                  onChange={handleRegisterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Choose a username"
                />
                <p className="mt-1 text-xs text-gray-500">
                  3+ characters, letters, numbers, underscores, or hyphens
                </p>
              </div>

              <div>
                <label htmlFor="reg-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="reg-email"
                  name="email"
                  type="email"
                  required
                  value={registerData.email}
                  onChange={handleRegisterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label htmlFor="reg-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  id="reg-name"
                  name="name"
                  type="text"
                  required
                  value={registerData.name}
                  onChange={handleRegisterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label htmlFor="reg-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="reg-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={registerData.password}
                    onChange={handleRegisterChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Choose a password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                <div className="mt-2 space-y-1">
                  <p className={`text-xs ${registerData.password.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
                    {registerData.password.length >= 8 ? '\u2713' : '\u2022'} At least 8 characters
                  </p>
                  <p className={`text-xs ${/[A-Za-z]/.test(registerData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                    {/[A-Za-z]/.test(registerData.password) ? '\u2713' : '\u2022'} Contains a letter
                  </p>
                  <p className={`text-xs ${/\d/.test(registerData.password) ? 'text-green-600' : 'text-gray-500'}`}>
                    {/\d/.test(registerData.password) ? '\u2713' : '\u2022'} Contains a number
                  </p>
                </div>
              </div>

              <div>
                <label htmlFor="reg-confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  id="reg-confirm-password"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={registerData.confirmPassword}
                  onChange={handleRegisterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Confirm your password"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating account...' : 'Create Account'}
              </button>
            </form>
          ))}
        </div>

        {supportsPasswordAuth && (
          <div className="text-center">
            <p className="text-xs text-gray-500">
              By continuing, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthForm;
